// Shared types for the ArchviewVR engine, viewer and dashboard.
// All positions are metres in three.js space (Y up). Headings are degrees in manifests, radians at runtime.

/** One panorama as published in a project manifest. */
export interface ManifestPano {
  id: string;
  /** File name of the render, e.g. ExportCam_001. */
  name: string;
  /** Room name set by the studio, e.g. Kitchen. Shown as a label in the tour. */
  room?: string;
  /** [x, y, z] in metres, Y up. y is the camera (eye) height above world zero. */
  position: [number, number, number];
  /** Heading in degrees: the world direction the centre of the image faces. 0 = -Z. */
  yaw: number;
  /** Index into manifest.levels, or null for in-between views such as stairs. */
  level: number | null;
  /** Panorama set, for day/night or scheme toggles. "default" for the main set. */
  set: string;
  images: {
    /** Small image used for blending everywhere (1K phone / 2K desktop). */
    base: string;
    /** Optional full-size image, swapped in when the viewer stops at this pano. */
    full?: string;
    /** Optional smaller image for phones. */
    mobile?: string;
    /** Optional depth map (PNG: millimetres from the camera as R·65536 + G·256 + B, top row first, white = nothing measured). */
    depth?: string;
  };
  /** Alternate panoramas taken from this same spot (another finish scheme, say). Same position and heading. */
  alts?: ManifestAlt[];
}

/** Another render at the same spot as its pano, shown when the buyer picks that scheme. */
export interface ManifestAlt {
  id: string;
  /** Scheme name, e.g. Dark. The same name at other spots switches with it. */
  set: string;
  images: { base: string; full?: string; mobile?: string };
}

export interface ManifestLevel {
  name: string;
  /** Typical eye height of the renders on this level, in metres. */
  height: number;
  /** No floor button for this level in the 3D plan (its renders can still be walked to). */
  hidden?: boolean;
}

export type HotspotKind = 'info' | 'image' | 'video' | 'product' | 'link';

export interface ManifestHotspot {
  id: string;
  kind: HotspotKind;
  /** World position of the spot on the surface, metres. */
  position: [number, number, number];
  /** Which way the surface faces at that spot (unit vector). */
  normal?: [number, number, number];
  /** Leader line: the marker sits this many metres out from the surface along the normal. 0 or missing = on the surface. */
  leader?: number;
  title: string;
  body?: string;
  images?: string[];
  video?: string;
  supplier?: string;
  price?: string;
  link?: { label: string; url: string };
  /** Ask for an email before the link opens. */
  leadGate?: boolean;
}

export interface ManifestBrand {
  /** Logo shown on the loading screen and in the top-left chip. */
  logo?: string;
  /** Logo for the dark theme (light-coloured), used instead of logo when theme is dark. */
  logoDark?: string;
  /** No logo on the loading screen or in the top-left chip. */
  hideLogo?: boolean;
  /** Loading screen title; the project name when empty. */
  title?: string;
  accent?: string;
  /** Typeface: a Google Fonts family name, or a font file (fontUrl) the studio uploaded. */
  font?: string;
  fontUrl?: string;
  loadingBackground?: string;
  /** A short looping video behind the loading screen (muted). */
  loadingVideo?: string;
  tagline?: string;
  /** Light (white panels) or dark (charcoal panels), and an optional text colour for panels. */
  theme?: 'light' | 'dark';
  text?: string;
  /** Button and panel corners. */
  shape?: 'round' | 'soft' | 'square';
  /** Wait on the loading screen with an Explore button, then fade slowly into the tour. */
  explore?: boolean;
  /** Enquire tab on the side of the tour; enquiries are emailed to the studio's nominated address. */
  enquire?: { label?: string };
  /** Background music loop (muted until the viewer turns it on). */
  music?: string;
  /** "Take this tour home": a QR code button on computers, so buyers carry on on their phone. */
  qr?: boolean;
  /** Kiosk mode (?kiosk=1): after `idle` seconds untouched the tour plays itself, `dwell` seconds a room. */
  kiosk?: { idle?: number; dwell?: number; qr?: boolean };
}

export interface ProjectManifest {
  version: 1;
  id: string;
  name: string;
  /** "trial" adds a watermark and blocks public sharing. */
  state: 'trial' | 'published' | 'paused';
  brand: ManifestBrand;
  /** A password-protected tour's public manifest: everything else comes after /api/unlock. */
  locked?: boolean;
  /** White label: no "Powered by ArchviewVR". Set by the server from the project (ArchviewVR admins only). */
  whiteLabel?: boolean;
  /** Proxy model. The dashboard exports GLB in metres, Y up; scale and zUp cover other sources. */
  model: { url: string; format: 'glb' | 'gltf' | 'obj'; scale?: number; zUp?: boolean };
  levels: ManifestLevel[];
  panos: ManifestPano[];
  hotspots: ManifestHotspot[];
  start?: { pano: string; heading: number; pitch: number };
  settings?: Partial<TourSettings>;
  lead?: { enabled: boolean; required: boolean; notifyEmail?: string };
}

export type TourMode = 'walk' | 'hop' | 'doll';

export interface TourSettings {
  fov: number;
  walkSpeed: number;
  glide: number;
  settle: boolean;
  markers: boolean;
  perPixel: boolean;
  occlusion: boolean;
  flip: boolean;
  debugColours: boolean;
  edges: boolean;
  spin: boolean;
  /** Size to swap in at rest: 0 = off. */
  sharpSize: number;
  /** Modes the tour offers, and the one it opens in. */
  modes: TourMode[];
  startMode: TourMode;
  /** Show the room name when the viewer arrives in a room. */
  roomLabels: boolean;
  /** Floor rings in the 3D plan. */
  planMarkers: boolean;
  /** Circles on the floor in walk mode, where each render was taken (hop mode always shows them). */
  walkMarkers: boolean;
  /** Walk mode blending: how strongly the nearest render wins over the others (1 soft, 2 normal, 5 crisp). */
  blendPower: number;
  /** Within this many metres of a render, show only that render (0 = off). */
  blendHold: number;
  /** The model was made from the depth maps, not a model file (measuring is approximate). */
  depthModel?: boolean;
  /** Use renders' depth maps when they have them (on unless false). */
  depth?: boolean;
  /** Depth mesh detail: low is quicker, high follows thin things better (phones stay at low or standard). */
  depthDetail?: 'low' | 'standard' | 'high';
  /** Depth clean-up, 0 (off) to 1 (strong): drops surfaces a nearby render sees straight through. Default 0.5. */
  depthClean?: number;
  // ---- viewer options (the engine ignores these)
  /** Phone joystick on the left or right. */
  joystickSide: 'left' | 'right';
  /** Drag the other way to look around. */
  invertLook: boolean;
  /** Slowly look around when the tour opens, until someone touches it. */
  autoRotate: boolean;
  /** Keep the controls hidden until a tap or click. */
  controlsOnTap: boolean;
  /** Floor buttons. */
  levelButtons: boolean;
  /** North arrow in the 3D plan, and which heading is north (degrees). */
  compass: boolean;
  north: number;
  /** Dock buttons. */
  snapshot: boolean;
  share: boolean;
  fullscreen: boolean;
  /** Measure tool: tap two points on the model for the distance. */
  measure: boolean;
  /** Help button: how to move around, for a computer or a phone. */
  help: boolean;
  /** 3D plan: a slider on the left that moves the slice through the model, down to the level's floor. */
  cutSlider: boolean;
  /** Phones use Balanced (4K) even when the tour is set to Sharpest, so they don't run out of memory. */
  phoneBalanced: boolean;
  /** Background music (file key, relative to the manifest), off until the viewer turns it on. */
  music?: string;
  /** Last day the tour can be viewed (YYYY-MM-DD, local time); empty for no end. */
  expires?: string;
  /** Alternate panoramas: the button name for the main renders (Default when empty). */
  schemeDefault?: string;
  /** Alternate panoramas: scheme names in button order. */
  schemes?: string[];
  /** Alternate panoramas: offer the before and after slider. */
  compare?: boolean;
}

/** Walking speeds in m/s: Slow, Normal, Brisk. */
export const WALK_SPEEDS = { slow: 0.45, normal: 0.9, brisk: 1.8 };
/** Tours saved before Oct 2026 stored the old Normal (1.3); it now means the new Normal. */
export const walkSpeedOf = (v: number | undefined): number => (v == null ? WALK_SPEEDS.normal : Math.abs(v - 1.3) < 0.01 ? WALK_SPEEDS.normal : v);

export const DEFAULT_SETTINGS: TourSettings = {
  fov: 90,
  walkSpeed: 0.9,
  glide: 1,
  settle: true,
  markers: true,
  perPixel: true,
  occlusion: true,
  flip: false,
  debugColours: false,
  edges: false,
  spin: true,
  sharpSize: 4096,
  modes: ['walk', 'hop', 'doll'],
  startMode: 'walk',
  roomLabels: true,
  planMarkers: true,
  walkMarkers: true,
  blendPower: 2,
  blendHold: 0,
  joystickSide: 'left',
  invertLook: false,
  autoRotate: false,
  controlsOnTap: false,
  levelButtons: true,
  compass: false,
  north: 0,
  snapshot: true,
  share: true,
  fullscreen: true,
  measure: true,
  help: true,
  cutSlider: true,
  phoneBalanced: true,
};
